import { getSiteConfig } from '@shared/config';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';
import { createSkippableDepositLimitSchema, type DepositLimitTranslate } from '../../DepositLimits/DepositLimitSchema';
import { createRegistrationAddressSchema } from '../../form-controls/Address/schema';
import { PersonalDetailsSchema } from './steps/PersonalDetails/schema';

/**
 * Field-specific API validation error mappings
 * Maps API field names and error codes to translation keys
 */
const getApiValidationTranslations = (t: (key: string) => string) => {
  return {
    // Personal details field-specific validations
    firstname: {
      invalid_value: t('validation.personalDetails.firstName.invalid_value'),
    },
    lastname: {
      invalid_value: t('validation.personalDetails.lastName.invalid_value'),
    },
    streetName: {
      invalid_value: t('validation.address.streetName.invalid_value'),
    },
    streetNr: {
      invalid_value: t('validation.address.houseNameNumber.invalid_value'),
    },
    city: {
      invalid_value: t('validation.address.city.invalid_value'),
    },
    mobilePhoneNumber: {
      invalid_value: t('validation.mobileNumber.number.invalid_value'),
    },
    // Add more field-specific mappings as needed
  };
};

const getFormSchema = (t: DepositLimitTranslate) => {
  const market = getSiteConfig().REGISTRATION;
  const privacyPolicySchema = market.requirePrivacyPolicyAcceptance
    ? z.boolean().refine(val => val === true, {
        message: t('validation.createAccountCheckboxes.required'),
      })
    : z.boolean();

  return z.object({
    // Create account step fields
    createAccount: z.object({
      password: z
        .string()
        .min(1, t('validation.password.required'))
        .min(8, t('validation.password.minLength'))
        .regex(/(?=.*[a-z])(?=.*[A-Z])/, t('validation.password.lowerUpper'))
        .regex(/(?=.*\d)/, t('validation.password.number'))
        .regex(/^\S+$/, t('validation.password.noWhitespace')),
      email: z.string().min(1, t('validation.email.required')).email(t('validation.email.format')),
      termsAndConditions: z.boolean().refine(val => val === true, {
        message: t('validation.createAccountCheckboxes.required'),
      }),
      privacyPolicy: privacyPolicySchema,
    }),
    // TODO: not part of Wave 0, because the Core Domain API is not yet implemented
    // otpValidation: z.object({
    //   otp: z.string().min(1, t('validation.otp.required')),
    // }),
    personalDetails: PersonalDetailsSchema(t),
    address: createRegistrationAddressSchema(t),
    addressLookup: z.string().optional(),
    mobileNumber: z
      .object({
        prefix: z.string().min(1, t('validation.phoneNumber.prefix.required')),
        number: z.string().min(1, t('validation.phoneNumber.number.required')),
      })
      .refine(
        data => {
          // Only validate format if both fields have values
          if (!data.prefix || !data.number) {
            return true;
          }

          try {
            const nationalDigits = data.number.replace(/\D/g, '');
            if (market.mobileNationalNumberRegex) {
              return market.mobileNationalNumberRegex.test(nationalDigits);
            }

            const fullPhoneNumber = `+${data.prefix.split('+')[1]}${data.number}`;
            return isValidPhoneNumber(fullPhoneNumber);
          } catch {
            return false;
          }
        },
        {
          message: t('validation.phoneNumber.invalid'),
          path: ['number'], // Show error on the number field
        }
      ),
    // Validation for productPreferences and channelPreferences is not necessary,
    // because we have the "Select all and continue" and "Don't send me offers" buttons
    productPreferences: z.array(z.string()),
    channelPreferences: z.array(z.string()),
    depositLimits: createSkippableDepositLimitSchema(t),
  });
};

type FormData = z.infer<ReturnType<typeof getFormSchema>>;

export { getApiValidationTranslations, getFormSchema };
export type { FormData };
